import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getTopicDetails, deleteTopic, getTopicDigestConditional } from '../services/topics';
import useLocalMutation from '../hooks/useLocalMutation';
import useOverlayedMutations from '../hooks/useOverlayedMutations';
import { overlayTopicDigest } from '../db/overlay';
import { useToast } from '../hooks/useToast';
import { useLocalFirstQuery } from '../hooks/useLocalFirstQuery';
import { useDigestHighlights } from '../hooks/useDigestHighlights';
import { useOnline } from '../hooks/useOnline';
import { TOPIC_DIGEST_TTL_MS, TOPIC_PAGE_TTL_MS } from '../db/freshness';
import { useTextSelection } from '../hooks/useTextSelection';
import { setTitle } from '../utils/environment';
import ShareContextMenu from '../components/ShareContextMenu';
import SharePreviewDialog from '../components/SharePreviewDialog';
import ChatPopup, { ChatFAB } from '../components/ChatPopup';
import useNavigation from '../hooks/useNavigation';
import TopicDigestSection from './TopicDigest';
import TopicArticlesList from './TopicArticlesList';
import PageHeader from '../components/PageHeader';
import PageLoading from '../components/PageLoading';
import PageErrorState from '../components/PageErrorState';
import { useConfirm } from '../hooks/useConfirm';
import { formatDateCompact } from '../utils/dateUtils';

// Shared payload builder for the two digest-highlight create call sites (the
// context-menu selection path and the per-item star button). Extracted so the
// selection path's payload construction is unit-testable without driving
// window.getSelection in jsdom.
export const buildDigestHighlightPayload = ({ topic, digest, section, text, articleId = null }) => ({
  article_id: articleId, // Link to article if available (enables dual display)
  topic_name: topic.label,
  digest_section: section,
  digest_text: text,
  digest_version: digest.generated_at,
});

const TopicDetail = () => {
  const { topicId } = useParams();
  const { goBack } = useNavigation();
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();
  const online = useOnline();
  const [deleting, setDeleting] = useState(false);
  const [showAll, setShowAll] = useState(() => {
    const savedToggle = sessionStorage.getItem(`topic-${topicId}-showAll`);
    return savedToggle === 'true';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [digestExpanded, setDigestExpanded] = useState(true);
  const [markingRead, setMarkingRead] = useState(false);
  const [regeneratingDigest, setRegeneratingDigest] = useState(false);
  // Optimistically treat the topic as fully read after queuing mark-all-read.
  // Held for the lifetime of this mount (reset only when the topic changes, see
  // below); durable counts are re-read on the next mount.
  const [optimisticAllRead, setOptimisticAllRead] = useState(false);
  const [highlightingItem, setHighlightingItem] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [capturedSelection, setCapturedSelection] = useState({
    text: '',
    markdown: '',
    hasMarkdown: false
  });
  const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { mutate } = useLocalMutation();
  const { mutations: pendingReads } = useOverlayedMutations({
    types: ['mark_read', 'mark_unread', 'mark_all_read'],
  });

  // Refs for digest section detection
  const whatsNewRef = useRef(null);
  const coreThemeRef = useRef(null);
  const recommendedReadsRef = useRef(null);

  // Text selection hook
  const {
    selectedText,
    selectedMarkdown,
    hasMarkdown,
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    hideSelection,
  } = useTextSelection();

  // Local-first digest hook.
  const digestFetch = useCallback(async ({ etag }) => {
    return getTopicDigestConditional(topicId, { etag });
  }, [topicId]);

  const digestToRecord = useCallback((payload, meta) => ({
    topic_id: meta.key,
    payload,
    etag: meta.etag,
    fetched_at: meta.fetched_at,
    content_hash: meta.content_hash,
  }), []);

  const { data: digest, loading: digestLoading, refresh: refreshDigest, apply: applyDigest } = useLocalFirstQuery({
    table: 'topic_digests',
    key: topicId,
    ttl: TOPIC_DIGEST_TTL_MS,
    fetch: digestFetch,
    toRecord: digestToRecord,
  });

  // Local-first article-list hook (design §9). The list renders from a cached
  // `paged_views` page index keyed by topic/page/mode so it works offline. The
  // hook serves cache-first then revalidates through the freshness gate; offline
  // an uncached page falls through to the "not downloaded" state below.
  const mode = showAll ? 'all' : 'unread';
  // Reason: pageSize is in the key because the user can change per-page (§9 /
  // MINOR-3). The prefetch writer only caches the size-20 page, so a non-20
  // pageSize gets its own key and correctly falls through to the offline
  // "not downloaded" state instead of reading a wrong-sized cached page.
  const pageKey = `topic:${topicId}:p${currentPage}:${mode}:s${pageSize}`;

  const pageFetch = useCallback(async () => {
    const payload = await getTopicDetails(topicId, {
      unread_only: !showAll,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
    });
    return { status: 200, etag: null, payload };
  }, [topicId, showAll, pageSize, currentPage]);

  // Store ONLY a lightweight page index: ordered IDs + list-row snapshots +
  // topic/pagination metadata. Deliberately NOT written to `articles` — those
  // rows hold list-row display fields, not full content, so they must never be
  // mistaken for a downloaded article (§7.1 / ArticleViewer offline card).
  const pageToRecord = useCallback((payload, meta) => ({
    key: meta.key,
    payload: {
      articleIds: (payload?.articles || []).map((a) => a?.id).filter((id) => id != null),
      articles: payload?.articles || [],
      label: payload?.label ?? null,
      unread_count: payload?.unread_count ?? null,
      article_count: payload?.article_count ?? null,
      total_articles: payload?.total_articles ?? (payload?.articles || []).length,
      pagination: payload?.pagination ?? null,
      // Reason: header date + metadata panel render from these (MAJOR-2). Without
      // them the offline page shows a broken date and a vanished metadata panel.
      created_at: payload?.created_at ?? null,
      topic_metadata: payload?.topic_metadata ?? null,
      algorithm: payload?.algorithm ?? null,
    },
    etag: meta.etag,
    fetched_at: meta.fetched_at,
    content_hash: meta.content_hash,
  }), []);

  const { data: topic, loading, error } = useLocalFirstQuery({
    table: 'paged_views',
    key: pageKey,
    ttl: TOPIC_PAGE_TTL_MS,
    fetch: pageFetch,
    toRecord: pageToRecord,
  });

  const pagination = topic?.pagination ?? null;

  // Local-first digest-highlight read surface (design §5C): cache-first render,
  // network as background hydrator, queued creates overlaid as neg-id rows from
  // db.highlights (written by applyDigestHighlightCreate).
  const { highlights: digestHighlights, isDigestItemHighlighted } = useDigestHighlights({
    scope: 'topic',
    topicName: topic?.label ?? null,
  });

  const topicView = useMemo(
    () => overlayTopicDigest(topic, pendingReads),
    [topic, pendingReads],
  );

  const handleDeleteTopic = async () => {
    if (!(await confirm({
      title: 'Delete topic?',
      message: `"${topic.label}" will be deleted. This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;

    try {
      setDeleting(true);
      await deleteTopic(topicId);
      showSuccess('Topic deleted successfully');
      goBack(); // Go back to previous page
    } catch (err) {
      console.error('Failed to delete topic:', err);
      showError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const articleNavContext = {
    from: 'topic',
    topicId: topicId,
    topicLabel: topic?.label
  };

  const articleNavPageState = {
    showAll: showAll,
    currentPage: currentPage,
    pageSize: pageSize
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (perPage) => {
    setPageSize(perPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleHighlightDigestItem = async (digestText, section, articleId = null) => {
    if (!digest || !topic) return;

    const itemKey = `${section}-${digestText}`;
    // Prevent duplicate requests from rapid clicks or React Strict Mode double-invocation
    if (highlightingItem === itemKey) return;
    setHighlightingItem(itemKey);

    try {
      const highlightData = buildDigestHighlightPayload({
        topic,
        digest,
        section,
        text: digestText,
        articleId,
      });

      await mutate({ type: 'digest_highlight_create', payload: highlightData });
      // The optimistic neg-id row written by the mutation layer surfaces
      // through useDigestHighlights, so the star fills immediately and the
      // post-drain refresh swaps in the confirmed server row — no read-back.
      showSuccess('Digest item highlighted');
    } catch (err) {
      console.error('Failed to highlight digest item:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setHighlightingItem(null);
    }
  };

  // Detect which digest section the selection is in
  const detectDigestSection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;

    // Check each section ref
    if (whatsNewRef.current?.contains(element)) return 'whats_new';
    if (coreThemeRef.current?.contains(element)) return 'core_theme';
    if (recommendedReadsRef.current?.contains(element)) return 'recommended_reads';

    return null;
  };

  // Handle highlight from context menu (text selection)
  const handleCreateHighlightFromSelection = async () => {
    if (!selectedText || !digest || !topic) {
      hideSelection();
      return;
    }

    const section = detectDigestSection();
    if (!section) {
      showError('Please select text from the digest content');
      hideSelection();
      return;
    }

    try {
      setIsCreatingHighlight(true);

      const highlightData = buildDigestHighlightPayload({
        topic,
        digest,
        section,
        text: selectedText,
      });

      await mutate({ type: 'digest_highlight_create', payload: highlightData });
      showSuccess('Text highlighted');
      hideSelection();
    } catch (err) {
      console.error('Failed to create highlight:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setIsCreatingHighlight(false);
    }
  };

  // Share functionality handlers
  const handleShareClick = () => {
    setCapturedSelection({
      text: selectedText,
      markdown: selectedMarkdown,
      hasMarkdown: hasMarkdown
    });
    setShareDialogOpen(true);
    hideSelection();
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setCapturedSelection({
      text: '',
      markdown: '',
      hasMarkdown: false
    });
  };

  const handleMarkAllRead = async () => {
    if (!topicId) return;

    try {
      setMarkingRead(true);
      const limit = 100;
      let offset = 0;
      let totalMarked = 0;
      let hasMore = true;
      while (hasMore) {
        const detail = await getTopicDetails(topicId, { unread_only: true, limit, offset });
        const ids = (detail.articles || []).map(a => a.id);
        if (ids.length === 0) break;
        await mutate({
          type: 'mark_all_read',
          payload: { topic_id: String(topicId), article_ids: ids },
        });
        totalMarked += ids.length;
        if (ids.length < limit) {
          hasMore = false;
        } else {
          offset += ids.length;
        }
      }
      showSuccess(totalMarked > 0 ? `Marked ${totalMarked} articles as read` : 'No unread articles to mark');

      // Reason: mutate() only queues the mark and fires a debounced, fire-and-forget
      // drain. Reflect the read state OPTIMISTICALLY instead of refetching now — an
      // immediate refetch races the async drain and replays the stale pre-mark
      // unread_count (leaving the digest section + "Mark All Read" button on screen).
      // The pendingReads effect refetches durable counts once the mark drains.
      if (totalMarked > 0) setOptimisticAllRead(true);
    } catch (err) {
      console.error('Failed to mark articles as read:', err);
      showError(err.message);
    } finally {
      setMarkingRead(false);
    }
  };

  const handleRegenerateDigest = async () => {
    if (!topicId || regeneratingDigest) return;
    if (!(await confirm({
      title: 'Regenerate digest?',
      message: 'This runs a fresh AI summary and replaces the current one.',
      confirmLabel: 'Regenerate',
    }))) return;

    try {
      setRegeneratingDigest(true);
      // Force path: bypass the ETag so the server always returns a freshly
      // generated digest (never a 304). Handle errors loudly here rather than
      // through refreshDigest, which silently keeps the stale cache on failure.
      const resp = await getTopicDigestConditional(topicId, { force: true });
      if (resp.status === 200 && resp.payload) {
        await applyDigest(resp.payload, { etag: resp.etag });
        showSuccess('Digest regenerated');
      } else {
        showError('Could not regenerate digest');
      }
    } catch (err) {
      console.error('Failed to regenerate digest:', err);
      showError(err.message || 'Failed to regenerate digest');
    } finally {
      setRegeneratingDigest(false);
    }
  };

  const formatSimilarity = (similarity) => {
    return Math.round(similarity * 100);
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.9) return 'text-fun-mint bg-emerald-50 dark:bg-emerald-950/30';
    if (similarity >= 0.8) return 'text-fun-mint bg-emerald-50 dark:bg-emerald-950/30';
    if (similarity >= 0.7) return 'text-primary-600 bg-primary-50 dark:bg-primary-950/30';
    if (similarity >= 0.6) return 'text-fun-peach bg-amber-50 dark:bg-amber-950/30';
    return 'text-secondary-text bg-surface-hover';
  };

  // Keep the document title in sync with the loaded page / error state.
  useEffect(() => {
    if (topic?.label) {
      setTitle(`${topic.label} · Read It Never`);
    } else if (error) {
      setTitle('Topic Not Found · Read It Never');
    }
  }, [topic?.label, error]);

  // Drop any optimistic all-read state when switching topics. We deliberately do
  // NOT reconcile mid-session by refetching: the queued mark drains asynchronously,
  // and pendingReads drops a mutation when it goes IN-FLIGHT (before the server
  // write), so any refetch keyed off it races the drain and replays stale counts.
  // The optimistic state holds for this mount; the next mount fetches durable data.
  useEffect(() => {
    setOptimisticAllRead(false);
  }, [topicId]);

  if (loading) {
    return <PageLoading message="Loading topic details..." />;
  }

  if (error || !topic) {
    // Offline with no cached page index: this page was never downloaded. Mirror
    // ArticleViewer's offline card (design §9) instead of the generic error so
    // the user gets an actionable "open it online first" hint, not a dead end.
    if (!online) {
      return (
        <PageErrorState
          backTo="/topics"
          backLabel="Back to topics"
          headerTitle="Topic"
          icon="📵"
          iconLabel="Offline"
          title="This page isn't downloaded"
          description="Open it online first to cache it for offline reading."
          action={{ onClick: goBack, label: 'Go Back' }}
        />
      );
    }
    return (
      <PageErrorState
        backTo="/topics"
        backLabel="Back to topics"
        headerTitle="Topic"
        icon="❌"
        iconLabel="Not found"
        title="Topic not found"
        description={(error && error.message) || 'The topic you\'re looking for doesn\'t exist.'}
        action={{ to: '/topics', label: 'Back to topics' }}
      />
    );
  }

  // Apply overlay so pending mark_read mutations are reflected in the article list.
  // After an optimistic mark-all-read, keep the list consistent with the hidden
  // digest section: the default (unread-only) view shows nothing; the show-all view
  // renders the items as read. This holds even after the drain clears the overlay.
  const overlayedArticles = (topicView ?? topic).articles ?? [];
  const filteredArticles = optimisticAllRead
    ? (showAll ? overlayedArticles.map((a) => ({ ...a, is_read: true })) : [])
    : overlayedArticles;

  return (
    <div className="min-h-screen bg-clay-bg">
      <PageHeader
        backTo="/topics"
        backLabel="Back to topics"
        title={topic.label}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2 sm:gap-4">
            <span className="badge-fun-accent">📄 {optimisticAllRead ? 0 : (topicView?.unread_article_ids?.length ?? topic.unread_count)} unread</span>
            <span>📚 {topic.article_count} total</span>
            <span>🗓️ {formatDateCompact(topic.created_at)}</span>
          </span>
        }
        actions={
          <button
            type="button"
            onClick={handleDeleteTopic}
            disabled={deleting}
            className="btn-clay-danger btn-clay-sm"
          >
            {deleting ? 'Deleting...' : '🗑️ Delete'}
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {/* Topic Metadata */}
        {topic.topic_metadata && (
          <div className="card-clay p-4 mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-semibold text-body-text">Algorithm:</span>
                <div className="text-secondary-text">{topic.algorithm}</div>
              </div>
              {topic.topic_metadata.silhouette_score && (
                <div>
                  <span className="font-semibold text-body-text">Quality Score:</span>
                  <div className="text-secondary-text">
                    {(topic.topic_metadata.silhouette_score * 100).toFixed(1)}%
                  </div>
                </div>
              )}
              <div>
                <span className="font-semibold text-body-text">Cluster Size:</span>
                <div className="text-secondary-text">{topic.topic_metadata.article_count}</div>
              </div>
              <div>
                <span className="font-semibold text-body-text">Generation:</span>
                <div className="text-secondary-text capitalize">
                  {topic.topic_metadata.generated_from || 'Unknown'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Digest */}
        <TopicDigestSection
          topic={optimisticAllRead ? { ...topic, unread_count: 0 } : topic}
          digest={digest}
          digestLoading={digestLoading}
          digestExpanded={digestExpanded}
          setDigestExpanded={setDigestExpanded}
          markingRead={markingRead}
          onMarkAllRead={handleMarkAllRead}
          regeneratingDigest={regeneratingDigest}
          onRegenerateDigest={handleRegenerateDigest}
          onFetchDigest={refreshDigest}
          onHighlightDigestItem={handleHighlightDigestItem}
          isDigestItemHighlighted={isDigestItemHighlighted}
          highlightingItem={highlightingItem}
          digestHighlights={digestHighlights}
          articleNavContext={articleNavContext}
          articleNavPageState={articleNavPageState}
          whatsNewRef={whatsNewRef}
          coreThemeRef={coreThemeRef}
          recommendedReadsRef={recommendedReadsRef}
        />

        {/* Articles List */}
        <TopicArticlesList
          topicId={topicId}
          articles={filteredArticles}
          showAll={showAll}
          setShowAll={setShowAll}
          pagination={pagination}
          loading={loading}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          articleNavContext={articleNavContext}
          articleNavPageState={articleNavPageState}
          formatDate={formatDateCompact}
          formatSimilarity={formatSimilarity}
          getSimilarityColor={getSimilarityColor}
        />
      </div>

      {/* Context Menu for text selection in digest */}
      {selectedText && digest && (
        <ShareContextMenu
          visible={contextMenuVisible}
          position={contextMenuPosition}
          onShare={handleShareClick}
          onHighlight={handleCreateHighlightFromSelection}
          onClose={hideSelection}
          hasMarkdown={hasMarkdown}
          showHighlight={true}
          isHighlighting={isCreatingHighlight}
          ref={contextMenuRef}
        />
      )}

      {/* Share Preview Dialog */}
      <SharePreviewDialog
        isOpen={shareDialogOpen}
        onClose={handleCloseShareDialog}
        selectedText={capturedSelection.text}
        selectedMarkdown={capturedSelection.markdown}
        hasMarkdown={capturedSelection.hasMarkdown}
        articleTitle={topic?.label}
        articleUrl={`/topics/${topicId}`}
      />

      {/* Chat FAB and Popup - only show when digest is available */}
      {topic && digest && (
        <>
          <ChatFAB
            onClick={() => setChatOpen(!chatOpen)}
            isOpen={chatOpen}
          />
          <ChatPopup
            isOpen={chatOpen}
            onClose={() => setChatOpen(false)}
            contextType="topic"
            contextId={topicId}
            contextTitle={topic.label || 'Topic'}
          />
        </>
      )}
    </div>
  );
};

export default TopicDetail;